import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import { BillingInputs } from 'store/customer/types'
import { BillingFieldKeys, InputGroupKeys } from 'store/customer/enums'
import FormButtons from '../../components/form-buttons/FormButtons'
import { useBeforeunload } from 'react-beforeunload'
import { StepProps } from '../types'
import { updateAllInputs } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { useRouter } from 'next/router'
import { checkIfProductIsDigitalOnly, getProductName } from 'utils/products'
import ChangeProductDrawer from 'features/registration/components/change-product-drawer/ChangeProductDrawer'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { Checkbox, Form, ToggleRadio } from '@boltenergy-be/design-system'
import { log, setLogContext } from 'utils/logging'
import { Criticality, Flow } from 'types/logging'
import SwitchProductCta from 'features/registration/components/switch-product-cta/SwitchProductCta'
import { Address, Iban } from 'components/inputs'
import { CONTACT_MODES, PAYMENT_METHODS, PRODUCT_CONFIG_OPTIONS, PRODUCTS_CONFIG } from '@boltenergy-be/shared'
import { CommonTrackingParams } from 'types/tracking'

const Billing = ({ nextStep }: StepProps) => {
  // Redux store
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const { identificationMethod } = useStoreSelector((store) => store.marketing)
  const { selectedProposition } = propositions
  const dispatch = useStoreDispatch()

  // Router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['registration', 'validation'])

  // Constants
  const isDigitalOnlyProduct = checkIfProductIsDigitalOnly(selectedProposition)
  const isDirectDebitOnly = PRODUCTS_CONFIG[selectedProposition][PRODUCT_CONFIG_OPTIONS.directDebitMandatory]

  // React Hook form
  const hookForm = useForm<BillingInputs>({
    defaultValues: {
      ...inputs[InputGroupKeys.BILLING],

      // Set default paymentMethod on digital if it's a digital only product
      [BillingFieldKeys.PAYMENT_METHOD]: isDirectDebitOnly
        ? PAYMENT_METHODS.domiciliation
        : inputs[InputGroupKeys.BILLING][BillingFieldKeys.PAYMENT_METHOD],

      // Set default deliveryMode on digital if it's a digital only product or Bolt Go simulationType
      [BillingFieldKeys.DELIVERY_MODE]: isDigitalOnlyProduct[PRODUCT_CONFIG_OPTIONS.billingOptions]
        ? CONTACT_MODES.email
        : inputs[InputGroupKeys.BILLING][BillingFieldKeys.DELIVERY_MODE]
    },
    mode: 'onBlur'
  })
  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch
  } = hookForm

  // React hook form watch values
  const watchPaymentMethod = watch('paymentMethod')
  const watchSameCorrespondenceAddress = watch('sameCorrespondenceAddress')

  // The return of the constants (defined here because of loading & usage order)
  const isIbanRequired = isDirectDebitOnly || watchPaymentMethod === PAYMENT_METHODS.domiciliation

  // Local state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [productDrawerOpen, setProductDrawerOpen] = useState<boolean>(false)

  /**
   * Prevents unload if one of the form fields is changed
   */
  useBeforeunload((e) => {
    if (isDirty) e.preventDefault()
  })

  /**
   * Handles the submit after validation
   *
   * @param data
   * @returns void
   */
  const onSubmit = (data: BillingInputs) => {
    try {
      setIsLoading(true)
      setLogContext('billing_form_input', data)

      const { correspondenceAddress, sameCorrespondenceAddress } = data

      // Update the fields in the store
      dispatch(
        updateAllInputs({
          inputs: {
            ...inputs,
            billing: {
              ...data,
              ...(!sameCorrespondenceAddress && { correspondenceAddress })
            }
          }
        })
      )

      // Track 'Subscription Step Finished' event @Semetis
      trackGTMEvent('subscription_step_finished:billing')
      trackEvent(
        { category: 'subscription', object: 'billing_step', action: 'finish' },
        { [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod }
      )

      // Go to the next step
      nextStep()
    } catch (error) {
      log({ error, identifier: `[${Flow.REGISTRATION}:createCustomer:billing]`, criticality: Criticality.HIGH })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider {...hookForm}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {/* DELIVERY MODE */}
        <Form.Fieldset
          title={
            isDigitalOnlyProduct[PRODUCT_CONFIG_OPTIONS.billingOptions]
              ? t('billing.sections.deliveryMode.digitalOnly')
              : t('billing.sections.deliveryMode.choice')
          }
        >
          {isDigitalOnlyProduct[PRODUCT_CONFIG_OPTIONS.billingOptions] ? (
            <p className="grid-col-full">
              {t('billing.fields.deliveryMode.digitalOnly.chosen', {
                product: getProductName(selectedProposition, locale)
              })}
              <SwitchProductCta
                openDrawer={() => setProductDrawerOpen(true)}
                paragraph={t('billing.fields.deliveryMode.digitalOnly.chooseAnother')}
              />
            </p>
          ) : (
            <ToggleRadio
              grid="full"
              label=""
              name={BillingFieldKeys.DELIVERY_MODE}
              options={[
                {
                  ...register(BillingFieldKeys.DELIVERY_MODE),
                  id: CONTACT_MODES.email,
                  label: t('billing.fields.deliveryMode.options.digital'),
                  value: CONTACT_MODES.email
                },
                {
                  ...register(BillingFieldKeys.DELIVERY_MODE),
                  id: CONTACT_MODES.post,
                  label: t('billing.fields.deliveryMode.options.paper'),
                  value: CONTACT_MODES.post
                }
              ]}
            />
          )}
        </Form.Fieldset>

        {/* CORRESPONDENCE ADDRESS */}
        <Form.Fieldset title={t('billing.sections.address')}>
          <Checkbox
            {...register(BillingFieldKeys.SAME_CORRESPONDENCE_ADDRESS)}
            className="grid-col-full"
            id={BillingFieldKeys.SAME_CORRESPONDENCE_ADDRESS}
            label={t('billing.fields.correspondenceAddress.sameAsDeliveryAddress')}
          />

          {!watchSameCorrespondenceAddress && <Address parentDotPath="correspondenceAddress" />}
        </Form.Fieldset>

        {/* PAYMENT METHOD */}
        <Form.Fieldset
          title={isDirectDebitOnly ? t('billing.sections.paymentMethod.directDebitOnly') : t('billing.sections.paymentMethod.choice')}
        >
          {!isDirectDebitOnly && (
            <ToggleRadio
              grid="full"
              label=""
              name={BillingFieldKeys.PAYMENT_METHOD}
              options={[
                {
                  ...register(BillingFieldKeys.PAYMENT_METHOD),
                  id: PAYMENT_METHODS.domiciliation,
                  label: t('billing.fields.paymentMethod.options.directDebit'),
                  value: PAYMENT_METHODS.domiciliation
                },
                {
                  ...register(BillingFieldKeys.PAYMENT_METHOD),
                  id: PAYMENT_METHODS.transfer,
                  label: t('billing.fields.paymentMethod.options.bankTransfer'),
                  value: PAYMENT_METHODS.transfer
                }
              ]}
            />
          )}

          <Iban {...(!isIbanRequired && { description: t('billing.fields.iban.refundDescription') })} required={isIbanRequired} />

          {isDirectDebitOnly && (
            <p className="grid-col-full">
              {t('billing.fields.paymentMethod.directDebitOnly.chosen', { product: getProductName(selectedProposition, locale) })}

              <SwitchProductCta
                openDrawer={() => setProductDrawerOpen(true)}
                paragraph={t('billing.fields.paymentMethod.directDebitOnly.chooseAnother')}
              />
            </p>
          )}

          {isIbanRequired && <p className="grid-col-full">{t('billing.fields.iban.mandateDescription')}</p>}
        </Form.Fieldset>

        <Form.Footer>
          <FormButtons {...{ isLoading }} />
        </Form.Footer>
      </Form>

      {productDrawerOpen && <ChangeProductDrawer isOpen={productDrawerOpen} onRequestClose={() => setProductDrawerOpen(false)} />}
    </FormProvider>
  )
}

export default Billing
